import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config';

import {
  ArrowLeft,
  CheckCircle,
  Lock,
  WarningCircle
} from '@phosphor-icons/react';
const AdminResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setIsSuccess(true);
      toast.success('Password reset successful!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/admin/login');
      }, 3000);
    } catch (error) {
      setError(error.message || 'Failed to reset password. Please try again.');
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center px-6">
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md relative z-10"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <img 
                src={`${process.env.PUBLIC_URL}/man-and-machine-logo.png`}
                alt="Man & Machine" 
                className="h-16 mx-auto mb-4"
              />
            </Link>
            <p className="text-sm text-[#A0AEC0]">Admin Portal</p>
          </div>

          <div className="glass rounded-3xl p-8 card-glow text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-[#14F195] to-[#6366F1] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">Password Reset!</h2>
            <p className="text-[#A0AEC0] mb-6">
              Your password has been successfully reset. Redirecting you to login...
            </p>

            <Link 
              to="/admin/login"
              className="inline-flex items-center justify-center w-full px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold"
            >
              Go to Login
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26] flex items-center justify-center px-6">
      
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <img 
              src={`${process.env.PUBLIC_URL}/man-and-machine-logo.png`}
              alt="Man & Machine" 
              className="h-16 mx-auto mb-4"
            />
          </Link>
          <p className="text-sm text-[#A0AEC0]">Admin Portal</p>
        </div>

        <div className="glass rounded-3xl p-8 card-glow">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Reset Password</h2>
          <p className="text-[#A0AEC0] text-center mb-6">
            Enter your new password below.
          </p>

          {error && !token && (
            <div className="flex items-center gap-3 p-4 mb-6 bg-red-500/10 border border-red-500/20 rounded-xl">
              <WarningCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {token && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <WarningCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#718096]" size={20} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full pl-12 pr-4 py-3 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                    placeholder="Enter new password"
                  />
                </div>
                <p className="text-xs text-[#718096] mt-1 ml-4">Minimum 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#718096]" size={20} />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full pl-12 pr-4 py-3 rounded-full glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors placeholder-[#718096]"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full relative overflow-hidden rounded-full group disabled:opacity-50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] via-[#8B5CF6] to-[#EC4899]"></div>
                <span className="relative z-10 block px-6 py-3 text-white font-semibold">
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </span>
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/admin/login" 
              className="inline-flex items-center text-sm text-[#6366F1] hover:text-[#818CF8] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminResetPassword;
